/**
 * HISIP-005: Creative Profiles Data Hooks
 * 
 * Provides CRUD operations for Creative Profiles with validation and error handling
 * Uses optimistic updates and proper error handling with toasts
 */

import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/App';
// HISI-FE-AUTH-01: Using cookie auth instead of tokens
import { useDashboardServerStore } from '@/stores/dashboard-server';
import { useServerAuth } from '@/hooks/useAuth-cookie';

export type Stage = 'script' | 'vision' | 'storyboard' | 'shots' | 'continuity' | 'frames' | 'video';
export type PresetKey = 'cinematic' | 'advertiser' | 'educational' | 'animated';

export interface CreativeProfile {
  id: string;
  project_id: string;
  name: string;
  description: string | null;
  preset_key: PresetKey | null;
  is_default: boolean;
  is_blank?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProfileStageSettings {
  id: string;
  profile_id: string;
  stage: Stage;
  writer_persona_id: string;
  evaluator_persona_id: string;
  rubric_id: string;
  provider: string;
  model: string;
  default_style_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProfileAllowedStyles {
  id: string;
  profile_id: string;
  style_id: string;
  created_at: string;
}

export interface CreateProfileRequest {
  name: string;
  description?: string;
  preset_key?: PresetKey;
  is_default?: boolean;
}

export interface StageSettingsInput {
  stage: Stage;
  writer_persona_id: string;
  evaluator_persona_id: string;
  rubric_id: string;
  provider: string;
  model: string;
  default_style_id?: string | null;
}

export interface ValidationError {
  field: string;
  code: string;
  message: string;
  details?: any;
}

// Simple in-memory cache for profile details to enable instant expands
type ProfileDetailsCacheEntry = {
  profile: CreativeProfile;
  stageSettings: ProfileStageSettings[];
  allowedStyles: ProfileAllowedStyles[];
  fetchedAt: number;
};

const profileDetailsCache: Record<string, ProfileDetailsCacheEntry> = {};
const PROFILE_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

export const prefetchProfileDetails = async (profileId: string) => {
  try {
    if (!profileId) return;
    const now = Date.now();
    const cached = profileDetailsCache[profileId];
    if (cached && now - cached.fetchedAt < PROFILE_CACHE_TTL_MS) return;

    // HISI-FE-AUTH-01: Cookie-based auth only
    const headers = {
      'Content-Type': 'application/json',
    } as HeadersInit;

    const [pRes, sRes, aRes] = await Promise.all([
      fetch(`${API_BASE_URL}/api/profiles/${profileId}`, { headers, credentials: 'include' }),
      fetch(`${API_BASE_URL}/api/profiles/${profileId}/stages`, { headers, credentials: 'include' }),
      fetch(`${API_BASE_URL}/api/profiles/${profileId}/allowed-styles`, { headers, credentials: 'include' })
    ]);

    if (!pRes.ok || !sRes.ok || !aRes.ok) return;
    const [p, s, a] = await Promise.all([pRes.json(), sRes.json(), aRes.json()]);
    if (!p.success || !s.success || !a.success) return;

    profileDetailsCache[profileId] = {
      profile: (p.data?.profile ?? p.data) as CreativeProfile,
      stageSettings: (s.data || []) as ProfileStageSettings[],
      allowedStyles: (a.data || []) as ProfileAllowedStyles[],
      fetchedAt: now,
    };
  } catch (e) {
    // silent prefetch failure
  }
};

/**
 * Hook for managing profiles list for a project
 */
// If projectId === 'owner', use owner/brand scoped endpoints
export const useProfiles = (projectId: string) => {
  const [profiles, setProfiles] = useState<CreativeProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { addNotification } = useDashboardServerStore();

  // HISI-FE-AUTH-01: Wait for authentication before making API calls
  const { user, loading: authLoading } = useServerAuth();

  const fetchProfiles = async () => {
    // owner/brand scoped list
    if (projectId === 'owner') {
      setLoading(true);
      setError(null);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      try {
        
        const response = await fetch(`${API_BASE_URL}/api/profiles`, {
          signal: controller.signal,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            
          },
        });
        if (!response.ok) throw new Error(`Failed to fetch profiles: ${response.status}`);
        const result = await response.json();
        if (!result.success) throw new Error(result.error || 'Failed to fetch profiles');
        setProfiles(result.data || []);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error fetching profiles (owner):', err);
        addNotification({ type: "error", title: `Failed to load profiles: ${errorMessage}`, duration: 4000 });
      } finally {
        setLoading(false);
      }
      return;
    }
    // project-scoped (legacy)
    if (!projectId) return;

    setLoading(true);
    setError(null);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/profiles`, {
        signal: controller.signal,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch profiles: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch profiles');
      }

      setProfiles(result.data || []);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching profiles:', err);
      addNotification({ type: "error", title: `Failed to load profiles: ${errorMessage}`, duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const createProfile = async (profileData: CreateProfileRequest): Promise<CreativeProfile | null> => {
    try {
      
      const url = projectId === 'owner'
        ? `${API_BASE_URL}/api/profiles`
        : `${API_BASE_URL}/api/projects/${projectId}/profiles`;
      const response = await fetch(url, {
        method: 'POST', credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create profile: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create profile');
      }

      const newProfile = result.data;
      setProfiles(prev => [...prev, newProfile]);
      addNotification({ type: "success", title: 'Profile created successfully', duration: 3000 });
      return newProfile;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error creating profile:', err);
      addNotification({ type: "error", title: `Failed to create profile: ${errorMessage}`, duration: 4000 });
      return null;
    }
  };

  const deleteProfile = async (profileId: string): Promise<boolean> => {
    try {
      // Optimistic update
      const previousProfiles = profiles;
      setProfiles(prev => prev.filter(p => p.id !== profileId));

      
      const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}`, {
        method: 'DELETE', credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          
        },
      });

      if (!response.ok) {
        // Revert on failure
        setProfiles(previousProfiles);
        throw new Error(`Failed to delete profile: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        // Revert on failure
        setProfiles(previousProfiles);
        throw new Error(result.error || 'Failed to delete profile');
      }

      addNotification({ type: "success", title: 'Profile deleted', duration: 3000 });
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error deleting profile:', err);
      addNotification({ type: "error", title: `Failed to delete profile: ${errorMessage}`, duration: 4000 });
      return false;
    }
  };

  const setDefaultProfile = async (profileId: string): Promise<boolean> => {
    try {
      // Optimistic update
      const previousProfiles = profiles;
      setProfiles(prev => prev.map(p => ({
        ...p,
        is_default: p.id === profileId
      })));

      
      const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}`, {
        method: 'PUT', credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({ is_default: true }),
      });

      if (!response.ok) {
        // Revert on failure
        setProfiles(previousProfiles);
        throw new Error(`Failed to set default profile: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        // Revert on failure
        setProfiles(previousProfiles);
        throw new Error(result.error || 'Failed to set default profile');
      }

      addNotification({ type: "success", title: 'Default profile updated', duration: 3000 });
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error setting default profile:', err);
      addNotification({ type: "error", title: `Failed to set default profile: ${errorMessage}`, duration: 4000 });
      return false;
    }
  };

  useEffect(() => {
    // Only fetch profiles after authentication is complete and user is authenticated
    if (!authLoading && user) {
      console.log('🏷️ Profiles: Auth complete, fetching profiles...', {
        projectId,
        userId: user.id,
        authLoading
      });
      fetchProfiles();
    } else if (!authLoading && !user) {
      console.log('🏷️ Profiles: Not authenticated, skipping fetch', {
        projectId,
        authLoading,
        hasUser: !!user
      });
      setError('Authentication required');
    } else {
      console.log('🏷️ Profiles: Waiting for auth...', {
        projectId,
        authLoading,
        hasUser: !!user
      });
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [projectId, authLoading, user]);

  return {
    profiles,
    loading,
    error,
    refetch: fetchProfiles,
    createProfile,
    deleteProfile,
    setDefaultProfile
  };
};

/**
 * Hook for managing individual profile data
 */
export const useProfile = (profileId: string | null) => {
  const [profile, setProfile] = useState<CreativeProfile | null>(null);
  const [stageSettings, setStageSettings] = useState<ProfileStageSettings[]>([]);
  const [allowedStyles, setAllowedStyles] = useState<ProfileAllowedStyles[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { addNotification } = useDashboardServerStore();

  // HISI-FE-AUTH-01: Wait for authentication before making API calls
  const { user, loading: authLoading } = useServerAuth();

  const fetchProfile = async () => {
    if (!profileId) {
      setProfile(null);
      setStageSettings([]);
      setAllowedStyles([]);
      return;
    }

    // Serve from cache immediately for responsive expand
    const cached = profileDetailsCache[profileId];
    if (cached) {
      setProfile(cached.profile);
      setStageSettings(cached.stageSettings);
      setAllowedStyles(cached.allowedStyles);
    }

    setLoading(!cached);
    setError(null);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Fetch profile, stage settings, and allowed styles in parallel
      
      const authHeaders = {
        'Content-Type': 'application/json',
        
      };
      const [profileRes, stageRes, stylesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/profiles/${profileId}`, {
          signal: controller.signal,
          credentials: 'include',
          headers: authHeaders,
        }),
        fetch(`${API_BASE_URL}/api/profiles/${profileId}/stages`, {
          signal: controller.signal,
          credentials: 'include',
          headers: authHeaders,
        }),
        fetch(`${API_BASE_URL}/api/profiles/${profileId}/allowed-styles`, {
          signal: controller.signal,
          credentials: 'include',
          headers: authHeaders,
        })
      ]);

      if (!profileRes.ok || !stageRes.ok || !stylesRes.ok) {
        throw new Error('Failed to fetch profile data');
      }

      const [profileResult, stageResult, stylesResult] = await Promise.all([
        profileRes.json(),
        stageRes.json(),
        stylesRes.json()
      ]);

      if (!profileResult.success || !stageResult.success || !stylesResult.success) {
        throw new Error('Invalid response from server');
      }

      const newProfile = (profileResult.data?.profile ?? profileResult.data) as CreativeProfile;
      const newStages = (stageResult.data || []) as ProfileStageSettings[];
      const newStyles = (stylesResult.data || []) as ProfileAllowedStyles[];
      setProfile(newProfile);
      setStageSettings(newStages);
      setAllowedStyles(newStyles);

      profileDetailsCache[profileId] = {
        profile: newProfile,
        stageSettings: newStages,
        allowedStyles: newStyles,
        fetchedAt: Date.now(),
      };
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      console.error('Error fetching profile:', err);
      addNotification({ type: "error", title: `Failed to load profile: ${errorMessage}`, duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<CreativeProfile>): Promise<boolean> => {
    if (!profileId) return false;

    try {
      
      const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}`, {
        method: 'PUT', 
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`Failed to update profile: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update profile');
      }

      setProfile(result.data);
      addNotification({ type: "success", title: 'Profile updated', duration: 3000 });
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error updating profile:', err);
      addNotification({ type: "error", title: `Failed to update profile: ${errorMessage}`, duration: 4000 });
      return false;
    }
  };

  const replaceStageSettings = async (stages: StageSettingsInput[]): Promise<{
    success: boolean;
    errors?: ValidationError[];
  }> => {
    if (!profileId) return { success: false };

    try {
      
      const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}/stages`, {
        method: 'PUT', credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({ stages }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update stages: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        if (result.errors) {
          // Return validation errors for UI display
          return { success: false, errors: result.errors };
        }
        throw new Error(result.error || 'Failed to update stages');
      }

      // Refetch to get updated data
      await fetchProfile();
      addNotification({ type: "success", title: 'Stage settings updated', duration: 3000 });
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error updating stage settings:', err);
      addNotification({ type: "error", title: `Failed to update stages: ${errorMessage}`, duration: 4000 });
      return { success: false };
    }
  };

  const replaceAllowedStyles = async (styleSlugs: string[]): Promise<{
    success: boolean;
    errors?: ValidationError[];
  }> => {
    if (!profileId) return { success: false };

    try {
      
      const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}/allowed-styles`, {
        method: 'PUT', credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({ style_slugs: styleSlugs }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update styles: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        if (result.errors) {
          return { success: false, errors: result.errors };
        }
        throw new Error(result.error || 'Failed to update styles');
      }

      // Refetch to get updated data
      await fetchProfile();
      addNotification({ type: "success", title: 'Allowed styles updated', duration: 3000 });
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error updating allowed styles:', err);
      addNotification({ type: "error", title: `Failed to update styles: ${errorMessage}`, duration: 4000 });
      return { success: false };
    }
  };

  const loadPreset = async (presetKey: PresetKey, preview = false): Promise<{
    success: boolean;
    preview?: any;
    errors?: ValidationError[];
    warnings?: string[];
  }> => {
    if (!profileId) return { success: false };

    try {
      
      const response = await fetch(`${API_BASE_URL}/api/profiles/${profileId}/load-preset`, {
        method: 'POST', credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify({ preset_key: presetKey, preview }),
      });

      if (!response.ok) {
        throw new Error(`Failed to load preset: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        if (result.errors) {
          return { success: false, errors: result.errors };
        }
        throw new Error(result.error || 'Failed to load preset');
      }

      if (preview) {
        return { success: true, preview: result.preview };
      }

      // Handle warnings (non-blocking notifications)
      if (result.warnings && result.warnings.length > 0) {
        result.warnings.forEach((warning: string) => {
          addNotification({ type: "warning", title: `Warning: ${warning}`, duration: 6000 });
        });
      }

      // Refetch to get updated data after applying preset
      await fetchProfile();
      addNotification({ type: "success", title: `${presetKey} preset loaded`, duration: 3000 });
      return { 
        success: true, 
        warnings: result.warnings 
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error loading preset:', err);
      addNotification({ type: "error", title: `Failed to load preset: ${errorMessage}`, duration: 4000 });
      return { success: false };
    }
  };

  useEffect(() => {
    // Only fetch profile after authentication is complete and user is authenticated
    if (!authLoading && user && profileId) {
      console.log('🏷️ Profile: Auth complete, fetching profile...', {
        profileId,
        userId: user.id,
        authLoading
      });
      fetchProfile();
    } else if (!authLoading && !user) {
      console.log('🏷️ Profile: Not authenticated, skipping fetch', {
        profileId,
        authLoading,
        hasUser: !!user
      });
      setError('Authentication required');
    } else {
      console.log('🏷️ Profile: Waiting for auth or profileId...', {
        profileId,
        authLoading,
        hasUser: !!user
      });
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [profileId, authLoading, user]);

  return {
    profile,
    stageSettings,
    allowedStyles,
    loading,
    error,
    refetch: fetchProfile,
    updateProfile,
    replaceStageSettings,
    replaceAllowedStyles,
    loadPreset
  };
};